import {
    AgingForecastRequest,
    AgingForecastService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/aging-forecast/aging-forecast';
import {
    EarlyPaidAdoptionRequest,
    EarlyPaidAdoptionService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/early-paid-adoption/early-paid-adoption';
import {
    EarlyPaidAmountRequest,
    EarlyPaidAmountService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/early-paid-amount/early-paid-amount';
import {
    WorkingCapitalLiftRequest,
    WorkingCapitalLiftService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/working-capital-lift/working-capital-lift';
import {
    SupplierDetailsRequest,
    SupplierDetailsService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/enrollment-details/enrollment-details';
import {
    SupplierEnrollmentsRequest,
    SupplierEnrollmentsService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/suppliers-enrollment/suppliers-enrollment';
import { SupplierDetialsResponse } from '@lsq-frontend/remote-services/analytics-graphql/src/lib/utils/types';
import {
    SpendVolumeRequest,
    SpendVolumeService
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/suppliers-spend-volume/suppliers-spend-volume';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import Highcharts from './high-charts-loader';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateFnsFormat, ignoreNullish } from '@lsq-frontend/common-ui';
import { ChartFilters, ColorRegistry, HighlightAnswer, ReportType } from './charts/chart-types';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ChartTableItem } from './chart-supplier-details/chart-supplier-details';
import { GetSpendVolumeOverviewSchema } from './charts/chart-spend-volume-overview';
import { GetSpendVolumeSchema } from './charts/chart-spend-volume';
import { GetTopSuppliersSchema } from './charts/chart-top-suppliers';
import { GetEarlyPaidAdoptionSchema } from './charts/chart-early-paid-adoption';
import { GetEarlyPaidAmountSchema } from './charts/chart-early-paid-amount';
import { GetSupplierEnrollmentsSchema } from './charts/chart-supplier-enrollments';
import { GetWorkingCapitalLiftSchema } from './charts/chart-working-capital-lift';
import { format } from 'date-fns';
import { GetAgingForecastSchema } from './charts/chart-aging-forecast';
import chartUtils from './charts/chart-utils';

// settings
Highcharts.setOptions({
    credits: {
        enabled: false
    }
});

@Component({
    selector: 'lsq-high-chart',
    styleUrls: ['./lsq-high-charts.scss'],
    template: `
        <div *ngIf="!!id" [ngClass]="{ 'high-chart': error }">
            <lsq-circle-loader *ngIf="isLoading && !error"></lsq-circle-loader>
            <form [formGroup]="formGroup" *ngIf="!error && !isLoading">
                <div class="high-chart-filters">
                    <div *ngIf="filterSuppliers && suppliers">
                        <span class="filter-title">Supplier:</span>
                        <lsq-multi-select
                            [formControlName]="'suppliers'"
                            [name]="'suppliers'"
                            [basicLabel]="true"
                            [options]="suppliers"
                            [initializeWithAllSelected]="true"
                            [class]="'suppliers-filter'"
                            [selectAllText]="'All Suppliers'"
                        ></lsq-multi-select>
                    </div>
                    <div *ngIf="highlightTop">
                        <span class="filter-title">Highlight By:</span>
                        <lsq-select
                            [formControlName]="'highlighted'"
                            [basicLabel]="true"
                            [options]="highlightOptions"
                            class="highlight-filter"
                        ></lsq-select>
                    </div>
                </div>
            </form>
            <div
                [id]="this.id"
                *ngIf="!error && !hideChart()"
                [style.height]="heigth"
                class="chart"
            ></div>
            <lsq-chart-supplier-details
                *ngIf="id === ReportType.SupplierEnrollmentDetails && tableData !== undefined"
                [items]="tableData"
            ></lsq-chart-supplier-details>
            <div *ngIf="error" class="hc-error">
                <img
                    class="no-data-image"
                    src="apps/fasttrack/src/assets/images/no-data-bars.png"
                    alt="No data to show for Spend Volume"
                />
                <h4 class="no-data-heading">No data available</h4>
            </div>
        </div>
    `
})
export class HighChartComponent implements AfterViewInit, OnInit {
    @Input() public id: string = '';
    @Input() public type: string = '';
    @Input() public filterSuppliers: boolean = false;
    @Input() public highlightTop: boolean = false;

    public ReportType = ReportType;
    public blacklistedCharts = [ReportType.SupplierEnrollmentDetails];
    public isLoading: boolean = true;
    public error: boolean = false;
    public chartInstance: Highcharts.Chart;
    public suppliers: string[];
    private defaultFilter = { highlighted: HighlightAnswer.No, suppliers: [] };
    public highlightOptions = [HighlightAnswer.No, HighlightAnswer.Yes];
    private gqlData: { [key: string]: string } = {};
    public colorRegistry: ColorRegistry = {};
    private heigth: string = '0px';
    private tableData: ChartTableItem[];
    private _spendVolumeRawData$ = this._spendVolumeService.dataStream.pipe(ignoreNullish());
    private _supplierEnrollmentsRawData$ = this._supplierEnrollmentsService.dataStream.pipe(
        ignoreNullish()
    );
    private _supplierEnrollmentDetailsRawData$ = this._supplierDetailsService.dataStream.pipe(
        ignoreNullish()
    );
    private _workingCapitalLiftRawData$ = this._workingCapitalLiftService.dataStream.pipe(
        ignoreNullish()
    );
    private _earlyPaidAdoptionRawData$ = this._earlyPaidAdoptionService.dataStream.pipe(
        ignoreNullish()
    );
    private _earlyPaidAmountRawData$ = this._earlyPaidAmountService.dataStream.pipe(
        ignoreNullish()
    );
    private _agingForecastRawData$ = this._agingForecastService.dataStream.pipe(ignoreNullish());
    public formGroup: FormGroup = new FormGroup({
        highlighted: new FormControl(HighlightAnswer.No, [Validators.required]),
        suppliers: new FormControl([])
    });

    constructor(
        private _userService: UserService,
        private _spendVolumeService: SpendVolumeService,
        private _supplierEnrollmentsService: SupplierEnrollmentsService,
        private _supplierDetailsService: SupplierDetailsService,
        private _workingCapitalLiftService: WorkingCapitalLiftService,
        private _earlyPaidAdoptionService: EarlyPaidAdoptionService,
        private _earlyPaidAmountService: EarlyPaidAmountService,
        private _agingForecastService: AgingForecastService
    ) {}

    public hideChart() {
        return this.blacklistedCharts.includes(this.id);
    }

    public reCreateChart(filters: ChartFilters = this.defaultFilter): void {
        try {
            const schema = this.getSchema(this.gqlData, filters);
            if (this.chartInstance) {
                this.chartInstance.destroy();
            }
            if (Object.keys(schema).length !== 0) {
                this.chartInstance = Highcharts.chart(this.id, schema as any);
            }
            this.isLoading = false;
        } catch (e) {
            console.error(e);
            this.error = true;
        }
    }

    public ngOnInit(): void {
        if (this.type !== ReportType.EarlyPaidAmount) {
            this.highlightOptions.push(HighlightAnswer.EarlyPaid);
        }
        this.formGroup.valueChanges.subscribe((filters: ChartFilters) => {
            this.reCreateChart(filters);
        });
        const dataSubscribers = (data: any) => {
            this.gqlData = data;
            this.reCreateChart();
        };
        switch (this.type) {
            case ReportType.SpendVolumeOverview:
            case ReportType.SpendVolume:
            case ReportType.TopSuppliers:
                this._spendVolumeRawData$.subscribe(dataSubscribers);
                break;
            case ReportType.WorkingCapitalLift:
                this._workingCapitalLiftRawData$.subscribe(dataSubscribers);
                break;
            case ReportType.EarlyPaidAdoption:
                this._earlyPaidAdoptionRawData$.subscribe(dataSubscribers);
                break;
            case ReportType.EarlyPaidAmount:
                this._earlyPaidAmountRawData$.subscribe(dataSubscribers);
                break;
            case ReportType.SupplierEnrollment:
                this._supplierEnrollmentsRawData$.subscribe(dataSubscribers);
                break;
            case ReportType.AgingForecast:
                this._agingForecastRawData$.subscribe(dataSubscribers);
                break;
            case ReportType.SupplierEnrollmentDetails:
                this._supplierEnrollmentDetailsRawData$.subscribe(
                    (data: SupplierDetialsResponse) => {
                        this.isLoading = false;
                        let items: ChartTableItem[] = [];
                        for (const item of data.supplierDetails) {
                            items.push({
                                companyName: item.name,
                                enrolledDate:
                                    item.enrollmentDate === '0001-01-01'
                                        ? ''
                                        : format(item.enrollmentDate, DateFnsFormat.ShortFormat),
                                invoiceTermsAvg: item.avgInvoiceTerms.toString(),
                                invoiceAmount: item.invoiceAmount.cents
                            });
                        }
                        this.tableData = items;
                    }
                );
                break;
        }
    }

    public ngAfterViewInit(): void {
        if (!!this.id) {
            this.requestAnalyticsData();
        }
    }

    public requestAnalyticsData() {
        // Send supplier filter in queries.
        switch (this.type) {
            case ReportType.WorkingCapitalLift:
                this._workingCapitalLiftService.execute(
                    new WorkingCapitalLiftRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
            case ReportType.EarlyPaidAdoption:
                this._earlyPaidAdoptionService.execute(
                    new EarlyPaidAdoptionRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
            case ReportType.EarlyPaidAmount:
                this._earlyPaidAmountService.execute(
                    new EarlyPaidAmountRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
            case ReportType.AgingForecast:
                this._agingForecastService.execute(
                    new AgingForecastRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
            case ReportType.SpendVolume:
            case ReportType.SpendVolumeOverview:
            case ReportType.TopSuppliers:
                this._spendVolumeService.execute(
                    new SpendVolumeRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
            case ReportType.SupplierEnrollment:
                this._supplierEnrollmentsService.execute(
                    new SupplierEnrollmentsRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
            case ReportType.SupplierEnrollmentDetails:
                this._supplierDetailsService.execute(
                    new SupplierDetailsRequest({
                        buyerKey: this._userService.getAlternateCompanyKey()
                    })
                );
                break;
        }
    }

    public getSchema(data: any = {}, filters: ChartFilters = this.defaultFilter): object {
        let schema: any;
        switch (this.type) {
            case ReportType.SpendVolumeOverview:
                schema = GetSpendVolumeOverviewSchema(data, filters);
                break;
            case ReportType.SpendVolume:
                schema = GetSpendVolumeSchema(data, filters, this);
                break;
            case ReportType.TopSuppliers:
                schema = GetTopSuppliersSchema(data, filters, this);
                break;
            case ReportType.WorkingCapitalLift:
                schema = GetWorkingCapitalLiftSchema(data, filters, this);
                break;
            case ReportType.EarlyPaidAdoption:
                schema = GetEarlyPaidAdoptionSchema(data, filters);
                break;
            case ReportType.EarlyPaidAmount:
                schema = GetEarlyPaidAmountSchema(data, filters, this);
                break;
            case ReportType.SupplierEnrollment:
                schema = GetSupplierEnrollmentsSchema(data, filters);
                break;
            case ReportType.AgingForecast:
                schema = GetAgingForecastSchema(data, filters, this);
                break;
        }
        if (schema.chart?.height) {
            this.heigth = schema.chart?.height;
        }
        return schema;
    }

    public setSuppliers(sortedSuppliers: string[]) {
        let used = 0;
        for (const supp of sortedSuppliers.slice(0, 10)) {
            this.colorRegistry[supp] = chartUtils.elevenColors[used];
            used += 1;
        }
        // finally sort alphabetically
        this.suppliers = sortedSuppliers.sort();
    }
}
